import React, { useEffect, useState, useRef, useCallback } from 'react';
import useSales from '../hooks/useSales';
import salesService from '../services/salesService';
import InvoiceSalesActions from '../components/invoiceSales/InvoiceSalesActions';
import InvoiceSalesTable from '../components/invoiceSales/InvoiceSalesTable';
import MessageAlert from '../components/common/MessageAlert';
import Pagination from '../components/common/Pagination';
import { useWarehouseContext } from '../context/WarehouseContext';
import { printTableData } from '../utils/tablePrintHelper';
import SuperAdminDeleteDialog from '../components/common/SuperAdminDeleteDialog';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'invoice_sales_page_size';

const InvoiceSalesPage = () => {
  const { t } = useTranslation();
  const { sales, total, loading, error, dateRange, fetchSales, updateDateRange, exportSalesDaybook, bulkDeleteSales } = useSales();
  const { userRole } = useRole();

  const [selectedSales, setSelectedSales] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [alertMessage, setAlertMessage] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const { selectedWarehouseIds } = useWarehouseContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
  });
  const searchDebounceRef = useRef(null);

  const handleSelectSale = useCallback((id, selected) => {
    setSelectedSales(prev => {
      if (selected) return [...prev, id];
      return prev.filter(saleId => saleId !== id);
    });
  }, []);

  const handleSelectAll = useCallback((selected, ids = []) => {
    if (selected) {
      setSelectedSales(prev => {
        const newSelection = [...prev];
        ids.forEach(id => {
          if (!newSelection.includes(id)) newSelection.push(id);
        });
        return newSelection;
      });
    } else {
      setSelectedSales(prev => prev.filter(id => !ids.includes(id)));
    }
  }, []);

  const handleBulkDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmBulkDelete = async (password, { revertStock = false } = {}) => {
    setIsDeleting(true);
    try {
      // Honour the stock-revert choice the dialog actually offered (default: keep
      // stock). It used to be dropped, so the toggle did nothing.
      const res = await bulkDeleteSales(selectedSales, password, { revertStock });
      if (res.success) {
        const deleted = res?.sales?.sales_orders_deleted ?? 0;
        setSelectedSales([]);
        setShowDeleteDialog(false);
        // Drop the daybook cache AND force a re-read. The hook already removed the rows
        // locally, but a plain reload would otherwise be served the pre-delete cached
        // page and the deleted invoices would reappear.
        salesService.clearCache();
        handleRefresh();
        // Always tell the operator the real outcome. A silent "nothing happened" (a
        // 200 that deleted 0, or a list that didn't visibly change) is exactly what led
        // to repeated re-deletes and the mass-deletion incident.
        if (deleted > 0) {
          setAlertMessage({ type: 'success', title: t('invoiceSales.page.header.title'),
            message: `${deleted} invoice${deleted === 1 ? '' : 's'} deleted.` });
        } else {
          setAlertMessage({ type: 'warning', title: t('invoiceSales.page.header.title'),
            message: 'No invoices were deleted — they may already have been removed. The list has been refreshed.' });
        }
      } else {
        setAlertMessage({ type: 'error', title: t('invoiceSales.page.header.title'),
          message: String(res.error || 'Failed to delete selected invoices') });
      }
    } catch (err) {
      setAlertMessage({ type: 'error', title: t('invoiceSales.page.header.title'),
        message: String(err?.message || 'An unexpected error occurred') });
    } finally {
      setIsDeleting(false);
    }
  };

  // Debounce search → reset to page 1
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchTerm]);

  // Fetch when page / search / dateRange changes
  useEffect(() => {
    const skip = (currentPage - 1) * pageSize;
    fetchSales(dateRange.from, dateRange.to, {
      skip,
      limit: pageSize,
      gst_included: true,
      challan_printed: true,
      ...(committedSearch && { search: committedSearch }),
    });
  }, [fetchSales, currentPage, committedSearch, dateRange.from, dateRange.to, selectedWarehouseIds, pageSize]);

  useEffect(() => {
    if (error) setAlertMessage({ type: 'error', title: t('invoiceSales.page.header.title'), message: error });
  }, [error]);

  const handleRefresh = () => {
    const skip = (currentPage - 1) * pageSize;
    fetchSales(dateRange.from, dateRange.to, {
      skip,
      limit: pageSize,
      gst_included: true,
      challan_printed: true,
      ...(committedSearch && { search: committedSearch }),
      force: true,
    });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
  };

  const handleFromDateChange = (fromDate) => {
    updateDateRange(fromDate, dateRange.to);
    setCurrentPage(1);
  };

  const handleToDateChange = (toDate) => {
    updateDateRange(dateRange.from, toDate);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const res = await salesService.getSalesDaybook(dateRange.from, dateRange.to, {
        skip: 0,
        limit: 500,
        force: true,
        gst_included: true,
        challan_printed: true,
        ...(committedSearch && { search: committedSearch }),
      });
      const raw = res?.data;
      const data = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw?.items) ? raw.items : (raw?.records || []));
      // Each row is a challan: `invoice_number` carries the DC number and
      // `order_number` the parent sales order. Only fields the /sales-daybook row
      // actually returns (the old layout printed the order number under "Challan No",
      // the DC number under "Bill No.", and three columns the endpoint never sends).
      const columns = [
        { id: 'date', label: t('invoiceSales.table.columns.date') },
        { id: 'invoice_number', label: t('commonCols.challanNo') },
        { id: 'order_number', label: t('commonCols.orderNo') },
        { id: 'tally_name', label: t('commonCols.name') },
        {
          id: 'total_quantity',
          label: t('commonCols.qty'),
          getValue: (r) => `${r.actual_quantity ?? 0}`,
        },
        { id: 'total_amount', label: t('commonCols.amount') },
        { id: 'vehicle_number', label: t('commonCols.truckNo') },
        { id: 'remark', label: t('commonCols.remarks') },
      ];
      printTableData(data, columns, `${t('invoiceSales.page.header.title')} (${dateRange.from} to ${dateRange.to})`);
    } catch (err) {
      setAlertMessage({ type: 'error', title: t('common.status.error'), message: 'Failed to load data for printing.' });
    } finally {
      setPrintLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await exportSalesDaybook(dateRange.from, dateRange.to, {
        gst_included: true,
        challan_printed: true,
        ...(committedSearch && { search: committedSearch }),
      });
      if (!res.success) {
        setAlertMessage({ type: 'error', title: 'Export Failed', message: res.error });
      }
    } catch (err) {
      setAlertMessage({ type: 'error', title: 'Export Error', message: 'An unexpected error occurred during export.' });
    } finally {
      setExportLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
      {alertMessage && (
        <div className="fixed top-4 right-4 z-50">
          {/* isVisible is required — without it MessageAlert renders nothing, so every
              success/error/warning here was silently swallowed. message is coerced to a
              string so a non-string error object can never throw in render. */}
          <MessageAlert
            isVisible
            type={alertMessage.type}
            title={alertMessage.title}
            message={typeof alertMessage.message === 'string' ? alertMessage.message : String(alertMessage.message ?? '')}
            onClose={() => setAlertMessage(null)}
          />
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('invoiceSales.page.header.title')}</h1>
            <p className="mt-2 text-gray-600">{t('invoiceSales.page.header.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <InvoiceSalesActions
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onRefresh={handleRefresh}
          onPrint={handlePrint}
          printLoading={printLoading}
          onExport={handleExport}
          exportLoading={exportLoading}
          fromDate={dateRange.from}
          toDate={dateRange.to}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          loading={loading}
          canExport={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.SALES_STAFF, USER_ROLES.OPERATIONS_MANAGER].includes(userRole)}
          onBulkDelete={handleBulkDelete}
          hasSelection={selectedSales.length > 0}
          selectionCount={selectedSales.length}
        />
      </div>

      <InvoiceSalesTable
        sales={sales}
        loading={loading}
        selectedSales={selectedSales}
        onSelectSale={handleSelectSale}
        onSelectAll={handleSelectAll}
        userRole={userRole}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={total}
        onPageChange={setCurrentPage}
        loading={loading}
        totalLabel="total records"
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {showDeleteDialog && (
        <SuperAdminDeleteDialog
          isOpen={showDeleteDialog}
          onCancel={() => setShowDeleteDialog(false)}
          onConfirm={handleConfirmBulkDelete}
          loading={isDeleting}
          typeLabel={`${selectedSales.length} Selected Invoices`}
        />
      )}
    </div>
  );
};

export default InvoiceSalesPage;
